import { useOrganizations } from '@contexts/OrganizationsContext';
import { signIn, signOut } from '@lib/auth';
import { Maybe } from '@lib/types/Maybe';
import { Organization, OrganizationRole } from '@lib/types/api/Organization';
import {
  HeaderLink,
  HeaderUserMenuItem,
  HeaderUserMenuSection,
} from '@lib/types/cms/Header';
import { Link } from '@lib/types/common/Link';
import { isDefined } from '@lib/util/isDefined';
import { interpolateStringWithData } from 'utils';
import {
  HeaderMenuButtonListItem,
  HeaderMenuLinkListItem,
} from '../Navigation/common/types';
import {
  UserMenuSection,
  UserMenuSectionDisplay,
  UserMenuSelectedItem,
} from './types';

enum CMSEventName {
  SIGN_IN = 'sign-in',
  NOTIFICATIONS = 'openNotifications',
  SIGN_OUT = 'sign-out',
  ASSIGN_SEATS = 'assign_seats',
}

type UserMenuEventHandlers = {
  setSelectedItem: (item: UserMenuSelectedItem) => void;
};

type UseFormatUserMenuItemsArgs = {
  sections: HeaderUserMenuSection[];
  eventHandlers: UserMenuEventHandlers;
};

function mapEventToClickHandler(eventName: string | undefined) {
  switch (eventName) {
    case CMSEventName.SIGN_OUT:
      return () => signOut();
    case CMSEventName.SIGN_IN:
      return () => signIn();
    default:
      return undefined;
  }
}

/**
 * Some links are only displayed in the User Menu when the user is a owner (or not) of the currently active organization.
 *
 * Some links have custom interpolation logic that needs to be used with the active org.
 *
 * For these special cases, we hardcode this logic rather than make very specific rules in the CMS that will only be true for these couple items.
 */
function filterLinksByOrganizationRole(
  link: HeaderMenuLinkListItem,
  activeOrganization: Maybe<Organization>
) {
  const isOwner = activeOrganization?.role === OrganizationRole.OWNER;
  if (
    (link.href === '/orders' || link.href === '/account/voucher') &&
    !isOwner
  ) {
    // only owners can see these links, return null if not an owner
    return null;
  }
  if (link.href === '/order-requests' && isOwner) {
    // everyone except owners can see this link, return null if an owner
    return null;
  }
  if (link.href.includes('seat_managements')) {
    if (!isOwner) {
      return null;
    }
    // assign seats links to https://id.unity.com/organizations/{{slug}}/manage_assets/seat_managements , which needs to include the org's slug
    return {
      ...link,
      href: interpolateStringWithData(link.href, {
        slug: activeOrganization.slug,
      }),
    };
  }

  return link;
}

function mapLinkToLinkItem(
  item: HeaderUserMenuItem & { type: 'link' }
): HeaderMenuLinkListItem {
  return {
    value: item.value,
    iconName: item.icon ?? '',
    href: item.url ?? '',
    openInNewTab: Boolean(item.openInNewTab),
  };
}

function mapEventToButtonItem(
  item: HeaderUserMenuItem & { type: 'event' }
): HeaderMenuButtonListItem | null {
  const handleSelect = mapEventToClickHandler(item.eventName);

  if (handleSelect) {
    return {
      value: item.value,
      iconName: item.icon ?? '',
      handleSelect,
    };
  }

  return null;
}

function mapFastActionDropdownMenuItemsToLinks(
  dropdownMenu: HeaderLink[] | undefined
): Link[] {
  return (
    dropdownMenu
      ?.map((dropdownMenuItem) => {
        if (!dropdownMenuItem) {
          return null;
        }

        return {
          value: dropdownMenuItem.value || '',
          href: dropdownMenuItem.url || '',
          openInNewTab: Boolean(dropdownMenuItem.openInNewTab),
          icon: dropdownMenuItem.icon || '',
        };
      })
      .filter(isDefined) ?? []
  );
}

function mapFastActionToButtonItem(
  item: HeaderUserMenuItem & { type: 'fastAction' },
  eventHandlers: UserMenuEventHandlers
): HeaderMenuButtonListItem | null {
  if (!item.topLevelLink) {
    return null;
  }

  return {
    value: item.topLevelLink.value,
    iconName: item.topLevelLink.icon ?? '',
    hasChildren: true,
    handleSelect: () =>
      eventHandlers.setSelectedItem({
        heading: item.topLevelLink?.value || '',
        links: mapFastActionDropdownMenuItemsToLinks(item.dropdownMenu),
      }),
  };
}

function formatItem(
  item: HeaderUserMenuItem | null,
  eventHandlers: UserMenuEventHandlers,
  activeOrganization: Maybe<Organization>
) {
  if (!item) {
    return null;
  }

  if (item.type === 'link') {
    const linkItem = mapLinkToLinkItem(item);
    return filterLinksByOrganizationRole(linkItem, activeOrganization);
  } else if (item.type === 'event') {
    return mapEventToButtonItem(item);
  } else if (item.type === 'fastAction') {
    return mapFastActionToButtonItem(item, eventHandlers);
  }

  // unsupported item type, return null to be filtered out
  return null;
}

export function formatSections(
  sections: HeaderUserMenuSection[],
  eventHandlers: UserMenuEventHandlers,
  activeOrganization: Maybe<Organization>
): UserMenuSection[] {
  return sections
    .map((section) => {
      const items = section.items;
      if (!items || items.length === 0) {
        return null;
      }

      const formattedItems = items
        .map((item) => ({
          isSignOut:
            item?.type === 'event' && item.eventName === CMSEventName.SIGN_OUT,
          formatted: formatItem(item, eventHandlers, activeOrganization),
        }))
        .filter(({ formatted }) => isDefined(formatted));

      return {
        display: section.display as UserMenuSectionDisplay,
        items: formattedItems.map(({ formatted }) => formatted!),
        signOutIndex: formattedItems.findIndex(({ isSignOut }) => isSignOut),
      };
    })
    .filter(isDefined);
}

/**
 * This function is used to format the user menu items from the CMS-agnostic Header type
 * and map any event handlers.
 */
export const useFormatUserMenuItems = ({
  sections,
  eventHandlers,
}: UseFormatUserMenuItemsArgs): UserMenuSection[] => {
  const { activeOrganization } = useOrganizations();

  return formatSections(sections, eventHandlers, activeOrganization);
};
